// react components
import React, {
    useEffect,
    useState,
} from 'react'
import {
    Dialog,
} from '@mui/material'
import axios from 'axios'
import {
    useDispatch,
    useSelector,
} from 'react-redux'
import Select from 'react-select'

// data
import {
    contentTypeModelTypeSite,
    defaultReduxState,
    reduxModalErrorEventHandlerSite,
} from 'data'

// pages
import {
    ErrorHelper825,
    FormLabel825,
    FormModal825,
    IconBlock825,
    ImageHelper825,
} from 'pages'

// services
import {
    axiosErrorHandlerSite,
    getApiUrlSiteV2,
    getAxiosHeadersSite,
} from 'services'

// props
type SelectListInputV2825Props = {
    apiUrl?: string | undefined
    clearable: boolean
    closeMenuOnSelect?: boolean
    defaultMenuIsOpen?: boolean
    disabled?: boolean
    disabledInputOnly?: boolean
    error?: string
    excludeArray?: number[]
    formContentType?: contentTypeModelTypeSite
    helperText?: string
    hideIfNone?: boolean
    label?: string
    labelNoOption?: string
    layout?: string
    marginBottom?: number
    multi?: boolean
    name: string
    newInterface?: any
    onChange: any
    openOnLoad?: boolean
    options?: any
    optionsSorted?: boolean
    placeholder?: string
    profileObject?: any
    selectMutlipleMax?: number
    showNone?: boolean
    value: any
}

// main
export const SelectListInputV2825: React.FC<SelectListInputV2825Props> = React.memo(({
    apiUrl,
    clearable,
    closeMenuOnSelect = true,
    defaultMenuIsOpen,
    disabled,
    disabledInputOnly,
    error,
    excludeArray,
    formContentType,
    helperText,
    hideIfNone,
    label,
    labelNoOption,
    layout,
    marginBottom,
    multi,
    name,
    onChange,
    options,
    optionsSorted,
    placeholder,
    profileObject,
    selectMutlipleMax,
    showNone,
    value,
}) => {

    const dispatch = useDispatch()
    const reduxAuth = useSelector((state: defaultReduxState) => state.reduxAuth)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)
    const reduxText = useSelector((state: defaultReduxState) => state.reduxText.data)

    const [newOptions, setNewOptions] = useState<any[]>(options || [])
    const [newValue, setNewValue] = useState<any>(value)
    const [showHelperForm, setShowHelperForm] = useState(false)

    useEffect(() => {
        getData()
    }, [
        apiUrl,
        disabled,
    ])

    useEffect(() => {
        setNewValue(value)
    }, [
        value,
    ])

    useEffect(() => {
        if (options) {
            if (optionsSorted) {
                setNewOptions(options.sort((a: any, b: any) => a.name?.localeCompare(b.name)))
            } else {
                setNewOptions(options)
            }
        }
    }, [
        options,
    ])

    function onInputChange(value: any) {
        try {
            if (value === newValue) return
            if (multi && selectMutlipleMax && value?.length > Number(selectMutlipleMax)) {
                setNewValue(newValue)
                return
            }
            setNewValue(value)
            onChange({
                name: name,
                value: value,
            })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'SelectListInputV2825',
                'onInputChange',
            ))
        }
    }

    function getData() {
        try {
            const refreshAxiosHeaders = getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch)
            if (options) return
            if (!apiUrl) return
            if (disabled) return
            setNewOptions([])
            const listArray: any = []
            const getUrl = getApiUrlSiteV2(apiUrl, reduxModalSite)
            if (process.env.NODE_ENV === 'development') console.log(name, getUrl)
            axios({
                headers: refreshAxiosHeaders,
                method: 'get',
                url: getUrl,
            })
                .then((response) => {
                    if (Array.isArray(response.data)) {
                        response.data.map((val: any) => {
                            listArray.push(val)
                            return false
                        })
                        if (showNone) {
                            listArray.push({
                                id: 'None',
                                name: `No ${labelNoOption} || 'options`,  // TO_LATER_TEXT
                                slug: 'None',
                            })
                        }
                        if (optionsSorted) {
                            setNewOptions(listArray.sort((a: any, b: any) => a.name?.localeCompare(b.name)))
                        } else {
                            setNewOptions(listArray)
                        }
                    }
                })
                .catch((error) => {
                    axiosErrorHandlerSite({
                        apiUrl: getUrl,
                        component: 'SelectListInputV2825',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'getData',
                    })
                })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'SelectListInputV2825',
                'getData',
            ))
        }
    }

    const customStyles = {
        clearIndicator: (provided: any, state: any) => ({
            ...provided,
            cursor: 'pointer',
        }),
        control: (provided: any, state: any) => ({
            ...provided,
            background: '#f2f2f2',
            border: '1px solid #e4e4e4',
            borderRadius: '8px',
            ':hover': {
                ...provided[':hover'],
                border: '1px solid #e4e4e4',
                boxShadow: 'none',
            },
            ':focus': {
                ...provided[':focus'],
                boxShadow: '0 0 0 2px #ffffff',
                outline: '1px solid #ffffff',
            },
        }),
        input: (provided: any, state: any) => ({
            ...provided,
            color: '#000000',
        }),
        menu: (provided: any, state: any) => ({
            ...provided,
            background: '#f2f2f2',
            borderRadius: '8px',
            boxShadow: '2px 6px 21px -2px rgba(0,0,0,0.75)',
            zIndex: 9999,
        }),
        menuList: (provided: any, state: any) => ({
            ...provided,
            boxShadow: '0 0 4px 2px rgba(var(--mo-color-mo-color-rgb), 0.70)',
            border: '1px solid var(--mo-color-mo-color)',
            maxHeight: '20vh',
            zIndex: 9999,
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            background: '#f2f2f2',
            borderBottom: '1px solid #e4e4e4',
            color: state.isSelected ? 'var(--mo-color-mo-color)' : '#000000',
            ':active': {
                ...provided[':active'],
                backgroundColor: '#f2f2f2',
                color: 'var(--mo-color-mo-color)',
            },
            ':hover': {
                ...provided[':hover'],
                backgroundColor: 'var(--mo-color-mo-color)',
                color: '#000000',
                cursor: 'pointer',
            },
        }),
        singleValue: (provided: any, state: any) => ({
            ...provided,
            color: '#000000',
        }),
    }

    function profileOptions(option: any) {
        let textToReturn = ''
        if (option.cities?.length > 0) {
            textToReturn += `${option.cities.filter((obj: any) => obj.active).map((val: any) => (val.name)).join(' - ')}`
        }
        if (option.countries?.length > 0) {
            textToReturn += `${textToReturn ? ' - ' : ''}${option.countries.map((val: any) => (val.name)).join(' - ')}`
        }
        return textToReturn
    }

    function formatOptionLabel(option: any) {
        if (layout) {
            return (
                <div className='select-list-input-layout-custom-label-825'>
                    <div
                        className={`slilcl-lcl-image-wrap${(option.get_image_xs || option.get_image_lq) ? '' : ' zarma'}`}
                        style={{
                            borderRadius: ['profile'].includes(layout) ? '50%' : '0%',
                        }}
                    >
                        {(option.get_image_xs || option.get_image_lq)
                            ? (
                                <ImageHelper825
                                    alt={option.name}
                                    className='slilcl-lcl-image'
                                    dominant_color={option.get_dominant_color}
                                    src={option.get_image_xs || option.get_image_lq}
                                />
                            ) : (
                                <span className='slilcl-image-zarma-name'>{option.name?.[0]}</span>
                            )}
                    </div>
                    <div className='slilcl-lcl-text-wrap'>
                        <p>{option.name}</p>
                        {layout !== 'portfolio' && (
                            option.profile && <p>{option.profile.name}</p>
                        )}
                        {layout === 'profile' && (
                            <React.Fragment>
                                <p className='slilcl-lcl-profile-text mo-clamp-web1'>
                                    {profileOptions(option)}
                                </p>
                                {option.profile_created_by && (
                                    <p className='slilcl-lcl-profile-text mo-clamp-web1'>
                                        {option.profile_created_by.name}
                                    </p>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                </div>
            )
        }
        return <p>{option.select_name || option.name}</p>
    }

    if (hideIfNone && newOptions.length === 0) return null

    if (label) {
        return (
            <div
                className={`input-wrap-825${disabled ? ' disabled' : ''}`}
                style={{
                    marginBottom: marginBottom ? `${marginBottom}px` : undefined,
                }}
            >
                <div className='input-label-wrap'>
                    <FormLabel825
                        helperText={helperText}
                        label={label}
                        name={name}
                    />
                </div>
                <Select
                    className={disabledInputOnly ? 'select-list-input-list-disabled-825' : ''}
                    closeMenuOnSelect={closeMenuOnSelect}
                    defaultMenuIsOpen={defaultMenuIsOpen}
                    formatOptionLabel={formatOptionLabel}
                    getOptionLabel={option => option.name}
                    getOptionValue={option => option.id}
                    isClearable={clearable}
                    isDisabled={disabled || disabledInputOnly}
                    isMulti={multi}
                    menuPlacement='auto'
                    name={name}
                    onChange={onInputChange}
                    options={excludeArray ? newOptions.filter(obj => !excludeArray.includes(obj.id)) : newOptions}
                    placeholder={placeholder || ''}
                    styles={customStyles}
                    value={newValue}
                />
                <ErrorHelper825 error={error} />
            </div>
        )
    }

    return (
        <div className='text-normal-and-rich-input-825'>
            <div className='tnari-input-wrap'>
                <Select
                    className={disabledInputOnly ? 'select-list-input-list-disabled-825' : ''}
                    closeMenuOnSelect={closeMenuOnSelect}
                    defaultMenuIsOpen={defaultMenuIsOpen}
                    formatOptionLabel={formatOptionLabel}
                    getOptionLabel={option => option.name}
                    getOptionValue={option => option.id}
                    isClearable={clearable}
                    isDisabled={disabled || disabledInputOnly}
                    isMulti={multi}
                    menuPlacement='auto'
                    name={name}
                    onChange={onInputChange}
                    options={excludeArray ? newOptions.filter(obj => !excludeArray.includes(obj.id)) : newOptions}
                    placeholder={placeholder || ''}
                    styles={customStyles}
                    value={newValue}
                />
            </div>
            {formContentType && (
                <div className='tnari-input-action'>
                    <IconBlock825
                        className='tnari-icon'
                        edit={false}
                        iconClass='mo-new-icon-plus-solid'
                        onClick={() => setShowHelperForm(true)}
                    />
                </div>
            )}
            {showHelperForm && formContentType && (
                <Dialog
                    className='form-container-modal-825'
                    classes={{ paper: 'mo-dialog-wrapper' }}
                    disableEnforceFocus
                    maxWidth='xl'
                    onClose={() => setShowHelperForm(false)}
                    open
                >
                    <FormModal825
                        contentType={formContentType}
                        contentTypeSettingsN={undefined}
                        objectId='new'
                        onCloseHelperModal={(e: boolean, bvalue: any) => {
                            setShowHelperForm(false)
                        }}
                        onRefresh={getData}
                        profileObject={profileObject}
                    />
                </Dialog>
            )}
        </div>
    )
})
